"use client";

import { AuthProvider, type AuthProviderProps } from "@repo/ui/providers/AuthProvider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { usePathname, useRouter } from "next/navigation";
// import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { NavigationGuardProvider } from "next-navigation-guard";
import { ThemeProvider } from "next-themes";
import { useCallback, useEffect } from "react";
import { usePageTrackerStore } from "react-page-tracker";
import { useStore } from "@/store";
import { ScrollLockProvider } from "./scroll-lock-provider";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      refetchInterval: false,
      enabled: true,
    },
  },
});

type ProvidersProps = Readonly<{
  children: React.ReactNode;
}>;

export default function Providers({ children }: ProvidersProps): React.ReactElement {
  const pageState = usePageTrackerStore((state) => state);
  const setUser = useStore((state) => state.setUser);
  const user = useStore((state) => state.user);
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    if (user && pathname === "/auth") {
      if (pageState.isFirstPage) router.push("/");
      else if (pageState.pageHistory.length > 1 && pageState.pageHistory[pageState.pageHistory.length - 2] === "/")
        router.replace("/");
      else router.back();
    }
  }, [user, pathname, pageState]);

  return (
    <NavigationGuardProvider>
      <QueryClientProvider client={queryClient}>
        {/* <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-right" /> */}
        <ThemeProvider attribute="class" defaultTheme="dark">
          <ScrollLockProvider>
            <AuthProvider
              onUpdateUser={useCallback<AuthProviderProps["onUpdateUser"]>((user) => {
                setUser(user);
              }, [])}
            >
              {children}
            </AuthProvider>
          </ScrollLockProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </NavigationGuardProvider>
  );
}
